import React, { useState, useEffect } from 'react';
import { autoFetchPlayer } from '../../api';

const POSITIONS = ['PG', 'SG', 'SF', 'PF', 'C', 'Combo Guard', 'Wing', 'Stretch 4'];

const PlayerEditor = ({ player, onSave, onCancel }) => {
  const isEditing = !!player;
  const [form, setForm] = useState({
    name: '',
    position: '',
    height: '',
    weight: '',
    jersey_number: '',
    school: '',
    class_year: new Date().getFullYear(),
    college: '',
    hometown: '',
    rating: 0,
    strengths: '',
    weaknesses: '',
    nba_comparison: '',
    scouting_report: '',
    image_url: '',
    is_high_school: true
  });
  const [autoFetching, setAutoFetching] = useState(false);
  const [autoFetchMsg, setAutoFetchMsg] = useState('');

  useEffect(() => {
    if (player) {
      setForm({
        name: player.name || '',
        position: player.position || '',
        height: player.height || '',
        weight: player.weight || '',
        jersey_number: player.jersey_number || '',
        school: player.school || '',
        class_year: player.class_year || new Date().getFullYear(),
        college: player.college || '',
        hometown: player.hometown || '',
        rating: player.rating || 0,
        strengths: player.strengths || '',
        weaknesses: player.weaknesses || '',
        nba_comparison: player.nba_comparison || '',
        scouting_report: player.scouting_report || '',
        image_url: player.image_url || '',
        is_high_school: player.is_high_school !== undefined ? player.is_high_school : true
      });
    }
  }, [player]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value
    }));
  };

  const handleAutoFetch = async () => {
    if (!form.name.trim()) {
      setAutoFetchMsg('Enter a player name first.');
      return;
    }
    setAutoFetching(true);
    setAutoFetchMsg('');
    try {
      const result = await autoFetchPlayer(form.name);
      if (result.found && result.data) {
        const d = result.data;
        setForm((prev) => ({
          ...prev,
          jersey_number: d.jersey_number || prev.jersey_number,
          school: d.school || prev.school,
          height: d.height || prev.height,
          weight: d.weight || prev.weight,
          position: d.position || prev.position
        }));
        setAutoFetchMsg('Player info auto-filled from database!');
      } else {
        setAutoFetchMsg('No auto-fill data found for this player.');
      }
    } catch (err) {
      setAutoFetchMsg('Auto-fetch failed: ' + err.message);
    }
    setAutoFetching(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.position || !form.class_year) {
      alert('Name, position, and class year are required.');
      return;
    }
    onSave({ ...form, id: player?.id });
  };

  return (
    <div className="editor-overlay">
      <div className="editor-card">
        <div className="editor-header">
          <h2>{isEditing ? 'Edit Prospect' : 'Add New Prospect'}</h2>
          <button className="btn-close" onClick={onCancel}>&times;</button>
        </div>

        <form onSubmit={handleSubmit} className="editor-form">
          <div className="form-row">
            <div className="form-group">
              <label>Name *</label>
              <input name="name" value={form.name} onChange={handleChange} required />
            </div>
            <div className="form-group" style={{ display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
              <div style={{ flex: 1 }}>
                <label>&nbsp;</label>
                <button
                  type="button"
                  className="btn-autofetch"
                  onClick={handleAutoFetch}
                  disabled={autoFetching}
                >
                  {autoFetching ? 'Fetching...' : 'Auto-Fetch Info'}
                </button>
              </div>
            </div>
          </div>
          {autoFetchMsg && (
            <div className={`autofetch-msg ${autoFetchMsg.includes('auto-filled') ? 'success' : ''}`}>
              {autoFetchMsg}
            </div>
          )}

          <div className="form-row">
            <div className="form-group">
              <label>Position *</label>
              <select name="position" value={form.position} onChange={handleChange} required>
                <option value="">Select position...</option>
                {POSITIONS.map((p) => (
                  <option key={p} value={p}>{p}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Class Year *</label>
              <input
                type="number"
                name="class_year"
                value={form.class_year}
                onChange={handleChange}
                min={2020}
                max={2040}
                required
              />
            </div>
          </div>

          <div className="form-row cols3">
            <div className="form-group">
              <label>Jersey #</label>
              <input name="jersey_number" value={form.jersey_number} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Height</label>
              <input name="height" value={form.height} onChange={handleChange} placeholder='6&apos;7"' />
            </div>
            <div className="form-group">
              <label>Weight</label>
              <input name="weight" value={form.weight} onChange={handleChange} placeholder="200 lbs" />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>School</label>
              <input name="school" value={form.school} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>College Commitment</label>
              <input name="college" value={form.college} onChange={handleChange} />
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Hometown</label>
              <input name="hometown" value={form.hometown} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Rating (0-100)</label>
              <div className="rating-input">
                <input
                  type="range"
                  name="rating"
                  min="0"
                  max="100"
                  value={form.rating}
                  onChange={handleChange}
                />
                <span className="rating-value">{form.rating}</span>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label>NBA Comparison</label>
            <input name="nba_comparison" value={form.nba_comparison} onChange={handleChange} />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="is_high_school"
                  checked={form.is_high_school}
                  onChange={handleChange}
                  style={{ width: 'auto', marginRight: '6px' }}
                />
                High School Player
              </label>
            </div>
          </div>

          <div className="form-group">
            <label>Strengths</label>
            <textarea name="strengths" value={form.strengths} onChange={handleChange} rows={3} />
          </div>
          <div className="form-group">
            <label>Weaknesses</label>
            <textarea name="weaknesses" value={form.weaknesses} onChange={handleChange} rows={3} />
          </div>
          <div className="form-group">
            <label>Scouting Report</label>
            <textarea name="scouting_report" value={form.scouting_report} onChange={handleChange} rows={4} />
          </div>
          <div className="form-group">
            <label>Image URL</label>
            <input name="image_url" value={form.image_url} onChange={handleChange} />
          </div>

          <div className="form-actions">
            <button type="button" className="btn-cancel" onClick={onCancel}>Cancel</button>
            <button type="submit" className="btn-save">
              {isEditing ? 'Update Prospect' : 'Create Prospect'}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .editor-overlay {
          min-height: calc(100vh - 65px);
          padding: 20px;
          display: flex;
          justify-content: center;
        }
        .editor-card {
          background: #1a1a2e;
          border: 1px solid #2a2a4a;
          border-radius: 16px;
          padding: 30px;
          width: 100%;
          max-width: 720px;
          align-self: flex-start;
        }
        .editor-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 24px;
        }
        .editor-header h2 { color: #fff; font-size: 1.3rem; }
        .btn-close {
          background: none;
          border: none;
          color: #8888aa;
          font-size: 1.5rem;
          cursor: pointer;
        }
        .btn-close:hover { color: #ff4444; }
        .editor-form { display: flex; flex-direction: column; gap: 14px; }
        .form-row { display: flex; gap: 14px; }
        .form-row > .form-group { flex: 1; }
        .form-row.cols3 > .form-group { flex: 1; }
        .form-group { display: flex; flex-direction: column; gap: 4px; }
        .form-group label {
          color: #aaaacc;
          font-size: 0.78rem;
          font-weight: 600;
        }
        .form-group input, .form-group select, .form-group textarea {
          padding: 9px 12px;
          border-radius: 8px;
          border: 1px solid #3a3a5a;
          background: #12122a;
          color: #e0e0e0;
          font-size: 0.9rem;
          outline: none;
          font-family: inherit;
          transition: border-color 0.2s;
        }
        .form-group input:focus, .form-group select:focus, .form-group textarea:focus {
          border-color: #f7971e;
        }
        .form-group textarea { resize: vertical; }
        .rating-input {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .rating-input input[type=range] { flex: 1; padding: 0; accent-color: #f7971e; }
        .rating-value {
          color: #ffd200;
          font-weight: 700;
          font-size: 1.1rem;
          min-width: 30px;
          text-align: right;
        }
        .btn-autofetch {
          padding: 9px 16px;
          border-radius: 8px;
          border: 1px solid #4caf50;
          background: transparent;
          color: #4caf50;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          white-space: nowrap;
          width: 100%;
        }
        .btn-autofetch:hover { background: rgba(76,175,80,0.1); }
        .btn-autofetch:disabled { opacity: 0.5; cursor: not-allowed; }
        .autofetch-msg {
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 0.8rem;
          background: rgba(136,136,170,0.1);
          color: #8888aa;
        }
        .autofetch-msg.success {
          background: rgba(76,175,80,0.15);
          color: #4caf50;
        }
        .form-actions {
          display: flex;
          gap: 12px;
          justify-content: flex-end;
          margin-top: 8px;
          padding-top: 16px;
          border-top: 1px solid #2a2a4a;
        }
        .btn-cancel {
          padding: 10px 24px;
          border-radius: 10px;
          border: 1px solid #3a3a5a;
          background: transparent;
          color: #aaaacc;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .btn-cancel:hover { border-color: #8888aa; color: #fff; }
        .btn-save {
          padding: 10px 24px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #f7971e, #ffd200);
          color: #1a1a2e;
          font-weight: 700;
          font-size: 0.9rem;
          cursor: pointer;
        }
        .btn-save:hover { opacity: 0.9; }
        @media (max-width: 600px) {
          .form-row { flex-direction: column; }
        }
      `}</style>
    </div>
  );
};

export default PlayerEditor;
